import {
  Clock,
  CheckSquare,
  BarChart3,
  Users,
  Settings,
  FileText,
  Calendar,
  DollarSign,
  Briefcase,
  TrendingUp,
  Bell,
  Download,
  Upload,
  Archive,
  Shield,
  Zap,
  BookOpen,
  HelpCircle,
  LayoutDashboard,
  Building2,
} from 'lucide-react';
import type { NavigationConfig } from '@/types/navigation';

/**
 * Navigation Configuration
 * 
 * This is the single source of truth for the application's navigation structure.
 * To add new menu items:
 * 1. Add the item to the appropriate section
 * 2. Define the path (route)
 * 3. Add an icon from lucide-react
 * 4. Optionally add children for sub-menus
 * 5. Add badges for notifications/counts
 */
export const navigationConfig: NavigationConfig = {
  sections: [
    // Main Navigation Section
    {
      id: 'main',
      items: [
        {
          id: 'dashboard',
          label: 'Dashboard',
          icon: LayoutDashboard,
          path: '/',
          description: 'Overview and key metrics',
        },
        {
          id: 'timesheet',
          label: 'My Timesheet',
          icon: Clock,
          path: '/timesheet',
          description: 'Enter and manage your time',
        },
        {
          id: 'approvals',
          label: 'Approvals',
          icon: CheckSquare,
          path: '/approvals',
          badge: 12, // Dynamic count from context
          badgeVariant: 'destructive',
          description: 'Review and approve timesheets',
        },
      ],
    },

    // Reports & Analytics Section
    {
      id: 'reports',
      title: 'Reports & Analytics',
      items: [
        {
          id: 'reports',
          label: 'Reports',
          icon: BarChart3,
          children: [
            {
              id: 'reports-timesheet',
              label: 'Timesheet Reports',
              icon: FileText,
              path: '/reports/timesheet',
              description: 'Detailed timesheet analysis',
            },
            {
              id: 'reports-utilization',
              label: 'Utilization',
              icon: TrendingUp,
              path: '/reports/utilization',
              description: 'Team utilization metrics',
            },
            {
              id: 'reports-billable',
              label: 'Billable Hours',
              icon: DollarSign,
              path: '/reports/billable',
              description: 'Billable vs non-billable analysis',
            },
          ],
        },
        {
          id: 'analytics',
          label: 'Analytics',
          icon: TrendingUp,
          path: '/analytics',
          description: 'Advanced analytics and insights',
        },
      ],
    },

    // Management Section
    {
      id: 'management',
      title: 'Management',
      items: [
        {
          id: 'team',
          label: 'Team',
          icon: Users,
          children: [
            {
              id: 'team-members',
              label: 'Team Members',
              icon: Users,
              path: '/team/members',
              description: 'Manage team members',
            },
            {
              id: 'team-schedule',
              label: 'Team Schedule',
              icon: Calendar,
              path: '/team/schedule',
              description: 'View team availability',
            },
            {
              id: 'team-capacity',
              label: 'Capacity Planning',
              icon: TrendingUp,
              path: '/team/capacity',
              description: 'Resource capacity planning',
            },
          ],
        },
        {
          id: 'projects',
          label: 'Projects',
          icon: Briefcase,
          children: [
            {
              id: 'projects-active',
              label: 'Active Projects',
              icon: Briefcase,
              path: '/projects/active',
              description: 'Currently active projects',
            },
            {
              id: 'projects-archive',
              label: 'Archived Projects',
              icon: Archive,
              path: '/projects/archive',
              description: 'Completed and archived projects',
            },
            {
              id: 'projects-budget',
              label: 'Budget Tracking',
              icon: DollarSign,
              path: '/projects/budget',
              description: 'Project budget and forecasting',
            },
          ],
        },
      ],
    },

    // Data & Integration Section
    {
      id: 'data',
      title: 'Data & Integration',
      items: [
        {
          id: 'integrations',
          label: 'Integrations',
          icon: Zap,
          path: '/integrations',
          badge: 'Live',
          badgeVariant: 'secondary',
          description: 'Manage companies and their system integrations',
        },
        {
          id: 'import-export',
          label: 'Import/Export',
          icon: Download,
          children: [
            {
              id: 'import',
              label: 'Import Data',
              icon: Upload,
              path: '/data/import',
              description: 'Import timesheet data',
            },
            {
              id: 'export',
              label: 'Export Data',
              icon: Download,
              path: '/data/export',
              description: 'Export to CSV, Excel, PDF',
            },
          ],
        },
      ],
    },

    // Administration Section
    {
      id: 'admin',
      title: 'Administration',
      items: [
        {
          id: 'settings',
          label: 'Settings',
          icon: Settings,
          children: [
            {
              id: 'settings-general',
              label: 'General',
              icon: Settings,
              path: '/settings/general',
              description: 'General application settings',
            },
            {
              id: 'settings-screens',
              label: 'Screen Data Modes',
              icon: LayoutDashboard,
              path: '/settings/screens',
              description: 'Configure data modes per screen',
            },
            {
              id: 'settings-roles',
              label: 'Roles & Rates',
              icon: Briefcase,
              path: '/settings/roles',
              description: 'Manage roles and billing rates',
            },
            {
              id: 'settings-approval',
              label: 'Approval Rules',
              icon: CheckSquare,
              path: '/settings/approval',
              description: 'Configure approval workflows',
            },
            {
              id: 'settings-notifications',
              label: 'Notifications',
              icon: Bell,
              path: '/settings/notifications',
              description: 'Notification preferences',
            },
            {
              id: 'settings-security',
              label: 'Security',
              icon: Shield,
              path: '/settings/security',
              description: 'Security and permissions',
            },
          ],
        },
      ],
    },
  ],
};

/**
 * Helper function to get all menu items as a flat array
 * Useful for searching or finding specific items
 */
export const getAllMenuItems = (): Array<{ item: any; path: string[] }> => {
  const items: Array<{ item: any; path: string[] }> = [];
  
  const traverse = (menuItems: any[], path: string[] = []) => {
    menuItems.forEach((item) => {
      items.push({ item, path: [...path, item.id] });
      if (item.children) {
        traverse(item.children, [...path, item.id]);
      }
    });
  };

  navigationConfig.sections.forEach((section) => {
    traverse(section.items, [section.id]);
  });

  return items;
};

/**
 * Helper function to find a menu item by ID
 */
export const findMenuItemById = (id: string) => {
  const allItems = getAllMenuItems();
  return allItems.find((item) => item.item.id === id);
};

/**
 * Helper function to get breadcrumb trail for a given path
 */
export const getBreadcrumbs = (currentPath: string) => {
  const breadcrumbs: any[] = [];
  
  const findPath = (items: any[], path: string, trail: any[] = []) => {
    for (const item of items) {
      const newTrail = [...trail, item];
      
      if (item.path === path) {
        breadcrumbs.push(...newTrail);
        return true;
      }
      
      if (item.children && findPath(item.children, path, newTrail)) {
        return true;
      }
    }
    return false;
  };

  navigationConfig.sections.forEach((section) => {
    findPath(section.items, currentPath);
  });

  return breadcrumbs;
};
